import type { ChangeEvent, ReactElement } from "react";

import { Trans } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Box, Flex, Text } from "@similarweb/ui-components/foundations";

import { translate } from "app/filters/i18nFilter";
import {
    StyledListItem,
    StyledTemplateLink,
} from "app/pages/sales-intelligence/modules/enrichment/components/UploadEnrichmentInstructions/style";
import type { EnrichmentType } from "app/pages/sales-intelligence/modules/enrichment/types/types";
import {
    FILE_STRUCTURE_FIRST_LAST_NAME,
    FILE_STRUCTURE_FULLNAME,
    FILE_STRUCTURE_HEADING,
    FILE_STRUCTURE_LINKEDIN,
    FILE_STRUCTURE_TABLE_COMPANY,
    FILE_STRUCTURE_TABLE_FIRST_NAME,
    FILE_STRUCTURE_TABLE_FULLNAME,
    FILE_STRUCTURE_TABLE_LAST_NAME,
    HELP_TEXT,
    MAXIMUM_CONTACTS_ROWS_TEXT,
    ONE_OF_THE_COLUMN_TEXT,
} from "app/pages/sales-intelligence/modules/enrichment/utils/i18n/modal.texts";
import enrichmentTrackingService from "app/pages/sales-intelligence/services/tracking/enrichment/trackingService";
import { MAX_CONTACTS_ROW_NUMBER } from "app/pages/sales-intelligence/sub-modules/management/components/constants";
import { formatNumberWithComa } from "app/pages/sales-intelligence/utils/leads";
import {
    StyledEnrichmentList,
    StyledSubList,
    StyledTable,
} from "app/pages/sales-intelligence/pages/management/components/Management/styles";

import type { ErrorTexts, ParsingErrors } from "../../types";
import { StyledLoadList } from "../style";

import { CSVFileInput } from "./CSVFileInput";

type UploadListProps = {
    handleFileChange: (event: ChangeEvent<HTMLInputElement>) => void;
    isLoading: boolean;
    hasParsingError: boolean;
    parsingErrors: ParsingErrors;
    errorTexts?: ErrorTexts;
    type?: EnrichmentType;
    dataAutomation?: string;
    customHint?: string | ReactElement;
    hintWidth?: number | string;
    hasCustomTracking?: boolean;
    hasHint?: boolean;
};

export const UploadContactsCSV = ({
    handleFileChange,
    errorTexts,
    isLoading,
    hasParsingError,
    parsingErrors,
    dataAutomation,
    customHint,
    hintWidth,
    hasCustomTracking,
    hasHint = true,
    type = "company",
}: UploadListProps) => {
    const handleTemplateDownload = () => {
        enrichmentTrackingService.csv.contacts.trackDownloadTemplate();
    };

    return (
        <StyledLoadList className="contact-enrichment">
            <Box
                width="341px"
                padding="0 24px 0 0"
                borderRight={`1px solid ${colorsPalettes.carbon[50]}`}
            >
                <StyledEnrichmentList>
                    <StyledListItem>{translate(ONE_OF_THE_COLUMN_TEXT)}</StyledListItem>
                    <StyledListItem>
                        {translate(MAXIMUM_CONTACTS_ROWS_TEXT, {
                            maxValue: formatNumberWithComa(MAX_CONTACTS_ROW_NUMBER),
                        })}
                    </StyledListItem>
                    <StyledListItem>{translate(FILE_STRUCTURE_HEADING)}</StyledListItem>
                    <Box margin="8px 0 0">
                        <StyledSubList>
                            <StyledListItem>{translate(FILE_STRUCTURE_FIRST_LAST_NAME)}</StyledListItem>
                        </StyledSubList>
                        <StyledTable quantity={3}>
                            <div className="header-cell">{translate(FILE_STRUCTURE_TABLE_FIRST_NAME)}</div>
                            <div className="header-cell">{translate(FILE_STRUCTURE_TABLE_LAST_NAME)}</div>
                            <div className="header-cell">{translate(FILE_STRUCTURE_TABLE_COMPANY)}</div>
                            <div>Darlene</div>
                            <div>Robertson</div>
                            <div>example.com</div>
                        </StyledTable>
                        <StyledSubList>
                            <StyledListItem>{translate(FILE_STRUCTURE_FULLNAME)}</StyledListItem>
                        </StyledSubList>
                        <StyledTable quantity={2}>
                            <div className="header-cell">{translate(FILE_STRUCTURE_TABLE_FULLNAME)}</div>
                            <div className="header-cell">{translate(FILE_STRUCTURE_TABLE_COMPANY)}</div>
                            <div>Darlene Robertson</div>
                            <div>Example name</div>
                        </StyledTable>
                        <StyledSubList>
                            <StyledListItem>{translate(FILE_STRUCTURE_LINKEDIN)}</StyledListItem>
                        </StyledSubList>
                        <StyledTable quantity={1}>
                            <div className="header-cell">Linkedin URL</div>
                            <div>https://www.linkedin.com/in/persona-profile-contact-48a636207/</div>
                        </StyledTable>
                    </Box>
                </StyledEnrichmentList>
            </Box>
            <Box width="283px">
                <Flex
                    alignItems="center"
                    justifyContent="center"
                    margin="0 0 16px"
                >
                    <Text fontVariant="body2">
                        <Trans
                            components={{
                                a: (
                                    <StyledTemplateLink
                                        download
                                        href="https://sales-workspace-staging.s3.us-east-1.amazonaws.com/ContactsEnrichment/contact_enrichment_template.csv"
                                        onMouseDown={handleTemplateDownload}
                                    />
                                ),
                            }}
                            i18nKey={HELP_TEXT}
                        />
                    </Text>
                </Flex>
                <CSVFileInput
                    hasCustomTracking={hasCustomTracking}
                    errorTexts={errorTexts}
                    data-automation={dataAutomation}
                    isLoading={isLoading}
                    hasError={hasParsingError}
                    parsingErrors={parsingErrors}
                    onChange={handleFileChange}
                    customHint={customHint}
                    hintWidth={hintWidth}
                    hasHint={hasHint}
                    type={type}
                />
            </Box>
        </StyledLoadList>
    );
};
